import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, RefreshCw, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/**
 * Limpopo Sales Insights — AI-authored monthly retail-trend report.
 * Combines the store's own figures with Limpopo-wide hardware market
 * context. Manual "Generate now" button + auto-runs on 1st of each month.
 */
export default function LimpopoInsights() {
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const [length, setLength] = useState("detailed");

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/limpopo-insights/list`);
      const items = r.data?.reports || [];
      setList(items);
      if (items.length > 0 && !current) {
        loadReport(items[0].id);
      }
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (id) => {
    try {
      const r = await axios.get(`${API}/limpopo-insights/${id}`);
      setCurrent(r.data);
    } catch {
      toast.error("Failed to load report");
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/limpopo-insights/generate`, { year, month, length });
      toast.success(`Report generated for ${MONTH_NAMES[month - 1]} ${year}`);
      setCurrent(r.data);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed — check EMERGENT_LLM_KEY balance");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, []);

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  const markdownToHtml = (md = "") => md
    .replace(/^### (.*)$/gm, "<h3 class='text-lg font-bold mt-4 mb-1 text-emerald-800'>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2 class='text-xl font-bold mt-6 mb-2 text-emerald-900'>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1 class='text-2xl font-bold mt-6 mb-3 text-emerald-900'>$1</h1>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/^- (.*)$/gm, "<li class='ml-5 list-disc'>$1</li>")
    .replace(/\n\n/g, "<br/><br/>");

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-emerald-900 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-emerald-600" /> Limpopo Sales Insights
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            AI-authored monthly retail trend report. Combines your store&apos;s data with Limpopo-wide market context.
            Auto-generated on the 1st of every month (SAST) and available on demand below.
          </p>
        </div>
      </div>

      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="limpopo-generate-card">
        <CardHeader>
          <CardTitle className="text-base">Generate a report on demand</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Year</label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[110px]" data-testid="limpopo-year"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Month</label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-[150px]" data-testid="limpopo-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Length</label>
            <Select value={length} onValueChange={setLength}>
              <SelectTrigger className="w-[160px]" data-testid="limpopo-length"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Short (executive)</SelectItem>
                <SelectItem value="detailed">Detailed (narrative)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="limpopo-generate-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Generate report
          </Button>
          <Button variant="ghost" onClick={fetchList} disabled={loading} data-testid="limpopo-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Past reports</CardTitle></CardHeader>
          <CardContent className="p-2 space-y-1 max-h-[520px] overflow-y-auto">
            {loading && <div className="text-sm text-gray-400 py-4 text-center">Loading…</div>}
            {!loading && list.length === 0 && (
              <div className="text-sm text-gray-500 p-3">No reports yet — hit Generate above.</div>
            )}
            {list.map((r) => (
              <button
                key={r.id}
                onClick={() => loadReport(r.id)}
                className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-emerald-50 border ${current?.id === r.id ? "bg-emerald-100 border-emerald-300" : "border-transparent"}`}
                data-testid={`limpopo-report-${r.id}`}
              >
                <div className="font-medium">{MONTH_NAMES[r.month - 1]} {r.year}</div>
                <div className="text-[10px] text-gray-500">Generated {new Date(r.generated_at).toLocaleDateString()}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 bg-white">
          <CardHeader>
            <CardTitle>
              {current ? `${MONTH_NAMES[current.month - 1]} ${current.year} — Limpopo Sales Insights` : "Select or generate a report"}
            </CardTitle>
            {current && (
              <div className="text-xs text-gray-500">
                Generated by {current.model} · {new Date(current.generated_at).toLocaleString()}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {current ? (
              <div
                className="prose prose-sm max-w-none leading-relaxed"
                data-testid="limpopo-report-body"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(current.body_markdown || "") }}
              />
            ) : (
              <div className="py-16 text-center text-gray-400 text-sm">
                No report open. Pick one from the left, or hit <strong>Generate report</strong> above.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
